import { useCallback, useEffect, useMemo, useState } from 'react';
import { uniqBy } from 'lodash';

import { useBrandVoicesAssets } from 'src/store/brandVoices/useBrandVoicesAssets';

import { useCustomFormatProps } from './useCustomFormatProps';

export const useFormatProps = ({
  localEditorRef,
  setSuccessMessage,
  setEditMode,
  readOnly,
  customFormatIds,
  isCurrentUserAdmin,
  assetType,
  setSelectedFormatIdsForAssetType,
  getSelectedFormatIdsForAssetType,
}) => {
  const [selectedFormatIds, setSelectedFormatIds] = useState(customFormatIds || []);

  const onSelectedFormatIdsChange = useCallback((ids) => {
    setSelectedFormatIds(ids);
  }, []);
  const { customFormats } = useBrandVoicesAssets();

  const customFormatProps = useCustomFormatProps({
    localEditorRef,
    setSuccessMessage,
    setEditMode,
    readOnly,
    customFormatIds,
  });

  // Compose formats list: AP style guide (if enabled OR user is admin) + all custom formats available for this request
  const availableFormats = useMemo(
    () => [
      ...uniqBy(
        customFormats
          .filter((f) => customFormatIds?.includes(f.id))
          .map((f) => ({ id: f.id, name: f.name, type: 'custom-format' })),
        'id'
      ),
    ],
    [customFormats, customFormatIds]
  );

  const handleSelectedFormatIdsChange = useCallback(
    async (ids) => {
      onSelectedFormatIdsChange(ids);
      if (setSelectedFormatIdsForAssetType && assetType) {
        await setSelectedFormatIdsForAssetType(assetType, ids);
      }
    },
    [assetType, setSelectedFormatIdsForAssetType, onSelectedFormatIdsChange]
  );

  // On mount or assetType change, load selection from ListViewStore (only if getSelectedFormatIdsForAssetType is available)
  useEffect(() => {
    const allIds = availableFormats.map((f) => f.id);

    if (!getSelectedFormatIdsForAssetType || !assetType) {
      onSelectedFormatIdsChange(allIds);
      return;
    }

    const fetchSelected = async () => {
      const ids = await getSelectedFormatIdsForAssetType(assetType);
      if (!ids || ids.length === 0) {
        onSelectedFormatIdsChange(allIds);
      } else {
        onSelectedFormatIdsChange(ids);
      }
    };
    fetchSelected();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetType, customFormatIds?.length, isCurrentUserAdmin, getSelectedFormatIdsForAssetType]);

  // Ensure selectedFormatIds only contains valid format IDs for this request
  useEffect(() => {
    const validFormatIds = availableFormats.map((f) => f.id);
    const filteredSelectedIds = selectedFormatIds.filter((id) => validFormatIds.includes(id));
    if (filteredSelectedIds.length !== selectedFormatIds.length) {
      onSelectedFormatIdsChange(filteredSelectedIds);
      if (setSelectedFormatIdsForAssetType && assetType) {
        setSelectedFormatIdsForAssetType(assetType, filteredSelectedIds);
      }
    }
  }, [
    customFormatIds,
    selectedFormatIds,
    availableFormats,
    assetType,
    setSelectedFormatIdsForAssetType,
    onSelectedFormatIdsChange,
  ]);

  return useMemo(() => {
    // Convert formatResults to the format expected by the UI
    const customFormatViolations =
      customFormatProps.formatResults?.flatMap(({ formatId, violations }) =>
        violations.map((violation) => ({
          ...violation,
          formatId,
        }))
      ) || [];

    return {
      // Custom Format props
      customFormatViolations,
      customFormatLoading: customFormatProps.loading,
      customFormatIds,
      onFormatFixClick: customFormatProps.onFormatFixClick,

      // Format selection props
      selectedFormatIds,
      onSelectedFormatIdsChange: handleSelectedFormatIdsChange,
      customFormats,
      availableFormats,

      // Common props
      readOnly,
      onMounted: () => {
        customFormatProps.onMounted();
      },
    };
  }, [
    customFormatProps,
    selectedFormatIds,
    customFormats,
    availableFormats,
    customFormatIds,
    handleSelectedFormatIdsChange,
    readOnly,
  ]);
};
